import { useState, useEffect } from 'react';
import { tutorApi } from '../services/api';
import {
  isNonSGTBrowser,
  todaySGT,
  formatDateTimeSGTLabelled,
  dateTimeToSGTIso,
  currentMonthYearSGT,
} from '../utils/dateUtils';
import '../assets/SubmitAttendance.css';

// True when viewer's browser is not in Asia/Singapore — drives the SGT badge visibility
const showSGTBadge = isNonSGTBrowser();

const SubmitAttendance = () => {
  const [tutees, setTutees] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [recentLessons, setRecentLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [endorsedMonths, setEndorsedMonths] = useState([]);
  
  const [formData, setFormData] = useState({
    tutee: '',
    subject: '',
    mode: '',
    date: todaySGT(),   // today's date in SGT, not UTC
    startTime: '',
    hours: 1,
    minutes: 0,
    remarks: ''
  });

  useEffect(() => {
    fetchTutees();
    fetchRecentLessons();
    fetchEndorsedMonths();
  }, []);

  const fetchTutees = async () => {
    try {
      const response = await tutorApi.getTutees();
      setTutees(response.data);
    } catch (err) {
      setError('Failed to load tutees');
    }
  };

  const fetchRecentLessons = async () => {
    try {
      // Use SGT month/year so the fetch doesn't shift near midnight
      const { month, year } = currentMonthYearSGT();
      const response = await tutorApi.getLessons(month, year);
      // Show last 10 lessons, sorted by most recent first
      const sortedLessons = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentLessons(sortedLessons.slice(0, 10));
    } catch (err) {
      // Silently fail - recent lessons are not critical
    }
  };

  const fetchEndorsedMonths = async () => {
    try {
      // Fetch monthly summaries to check which months are endorsed
      const response = await tutorApi.getMonthlySummaries();
      const endorsed = response.data
        .filter(summary => summary.endorsementStatus === 'endorsed')
        .map(summary => `${summary.year}-${String(summary.month).padStart(2, '0')}`);
      setEndorsedMonths(endorsed);
    } catch (err) {
      // Silently fail - will still validate on backend
      console.error('Failed to fetch endorsed months:', err);
    }
  };

  const isMonthEndorsed = (dateString) => {
    const date = new Date(dateString);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return endorsedMonths.includes(monthKey);
  };

  const handleTuteeChange = (tuteeId) => {
    setFormData({ ...formData, tutee: tuteeId, subject: '', mode: '' });
    
    // Find selected tutee and get their subjects
    const selectedTutee = tutees.find(t => t._id === tuteeId);
    if (selectedTutee) {
      // Get subjects where this tutor is assigned
      const user = JSON.parse(localStorage.getItem('user'));
      const tutorId = user._id;
      
      const tutorSubjects = selectedTutee.subjects.filter(subject => 
        subject.isActive && subject.assignedTutors?.some(at => {
          const tid = typeof at === 'object' ? (at.tutor?._id || at.tutor) : at;
          return tid === tutorId;
        })
      );
      
      setSubjects(tutorSubjects);
    } else {
      setSubjects([]);
    }
  };

  const handleSubjectChange = (subjectName) => {
    const selectedSubject = subjects.find(s => s.name === subjectName);
    
    if (selectedSubject) {
      // Get current user to find their assignment
      const user = JSON.parse(localStorage.getItem('user'));
      const tutorId = user._id;
      
      // Find the tutor's assignment for this subject
      const myAssignment = selectedSubject.assignedTutors?.find(at => {
        const tid = typeof at === 'object' ? (at.tutor?._id || at.tutor) : at;
        return tid === tutorId;
      });
      
      // Auto-populate based on schedule and session duration
      let autoStartTime = formData.startTime;
      let autoHours = formData.hours;
      let autoMinutes = formData.minutes;
      
      // If there are schedules, try to find one for today
      if (myAssignment?.schedules && myAssignment.schedules.length > 0) {
        const today = new Date(formData.date);
        const dayName = today.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', weekday: 'long' });
        
        // Find schedule matching today's day
        const todaySchedule = myAssignment.schedules.find(s => s.day === dayName && s.startTime);
        
        if (todaySchedule) {
          autoStartTime = todaySchedule.startTime;
        } else if (myAssignment.schedules[0]?.startTime) {
          // If no match for today, use the first schedule's time
          autoStartTime = myAssignment.schedules[0].startTime;
        }
      }
      
      // Auto-populate duration from sessionDuration
      if (selectedSubject.sessionDuration && selectedSubject.sessionDuration > 0) {
        const duration = parseFloat(selectedSubject.sessionDuration);
        autoHours = Math.floor(duration);
        autoMinutes = Math.round((duration - autoHours) * 60);
      }
      
      // Capitalize mode to match database enum (Physical/Online)
      const capitalizedMode = selectedSubject.mode?.charAt(0).toUpperCase() + selectedSubject.mode?.slice(1).toLowerCase();
      
      setFormData({ 
        ...formData, 
        subject: subjectName,
        mode: capitalizedMode,
        startTime: autoStartTime,
        hours: autoHours,
        minutes: autoMinutes
      });
    } else {
      setFormData({ 
        ...formData, 
        subject: subjectName,
        mode: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Check if the selected month is endorsed
    if (isMonthEndorsed(formData.date)) {
      const date = new Date(formData.date);
      const monthName = date.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', month: 'long', year: 'numeric' });
      setError(`Cannot submit attendance for ${monthName}. You have already endorsed your monthly summary for this period. Please contact admin if you need to make corrections.`);
      setLoading(false);
      return;
    }

    try {
      // Find the selected tutee and subject to get the rate
      const selectedTutee = tutees.find(t => t._id === formData.tutee);
      const selectedSubject = selectedTutee?.subjects.find(s => s.name === formData.subject);
      
      if (!selectedSubject) {
        throw new Error('Selected subject not found');
      }

      // Convert hours and minutes to total minutes
      const totalMinutes = (parseInt(formData.hours) * 60) + parseInt(formData.minutes || 0);

      // Treat the user's date + time input as SGT, then convert to UTC ISO for the backend.
      // dateTimeToSGTIso appends '+08:00' before parsing, so this is correct regardless
      // of what timezone the user's browser is set to.
      const timeStr = formData.startTime || '12:00';
      const dateTimeISO = dateTimeToSGTIso(formData.date, timeStr);

      const lessonData = {
        tutee: formData.tutee,
        subject: formData.subject,
        date: dateTimeISO,
        duration: totalMinutes,
        mode: formData.mode,
        summary: formData.remarks || '',
        rate: selectedSubject.rate
      };

      await tutorApi.submitLesson(lessonData);
      
      setSuccess('Attendance submitted successfully!');
      
      // Reset form
      setFormData({
        tutee: '',
        subject: '',
        mode: '',
        date: todaySGT(),   // reset to today in SGT
        startTime: '',
        hours: 1,
        minutes: 0,
        remarks: ''
      });
      setSubjects([]);
      
      // Refresh recent lessons with a small delay to ensure backend has processed
      setTimeout(() => {
        fetchRecentLessons();
      }, 300);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit attendance');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // Always display in SGT; appends "(SGT)" when user is outside Singapore
    return formatDateTimeSGTLabelled(dateString);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="submit-attendance">
      <div className="attendance-header">
        <h1>Submit Attendance</h1>
        <p>Record your completed lesson sessions</p>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="attendance-content">
        <div className="attendance-form-section">
          <form onSubmit={handleSubmit} className="attendance-form">
            <div className="form-group">
              <label htmlFor="tutee">Tutee *</label>
              <select
                id="tutee"
                value={formData.tutee}
                onChange={(e) => handleTuteeChange(e.target.value)}
                required
              >
                <option value="">Select tutee</option>
                {tutees.map(tutee => (
                  <option key={tutee._id} value={tutee._id}>
                    {tutee.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                  required
                  disabled={!formData.tutee}
                >
                  <option value="">Select subject</option>
                  {subjects.map((subject, idx) => (
                    <option key={idx} value={subject.name}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="mode">Mode *</label>
                <select
                  id="mode"
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                  required
                  disabled={!formData.subject}
                >
                  <option value="">Select mode</option>
                  {formData.subject && (() => {
                    const selectedSubject = subjects.find(s => s.name === formData.subject);
                    if (!selectedSubject) return null;
                    
                    const subjectMode = selectedSubject.mode?.toLowerCase();
                    
                    // If subject supports both modes, show both options
                    if (subjectMode === 'both') {
                      return (
                        <>
                          <option value="Online">Online</option>
                          <option value="Physical">Physical</option>
                        </>
                      );
                    }
                    
                    // Otherwise, only show the subject's mode (capitalize first letter to match enum)
                    const capitalizedMode = selectedSubject.mode?.charAt(0).toUpperCase() + selectedSubject.mode?.slice(1).toLowerCase();
                    return (
                      <option value={capitalizedMode}>
                        {capitalizedMode}
                      </option>
                    );
                  })()}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">
                  Date *
                  {showSGTBadge && <span className="sgt-badge">SGT</span>}
                </label>
                <input
                  type="date"
                  id="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className={isMonthEndorsed(formData.date) ? 'date-endorsed' : ''}
                />
                {isMonthEndorsed(formData.date) && (
                  <div className="field-warning">
                    This month has been endorsed. Contact admin for corrections.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="startTime">
                  Start Time *
                  {showSGTBadge && <span className="sgt-badge">SGT</span>}
                </label>
                <input
                  type="time"
                  id="startTime"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  required
                />
                {showSGTBadge && (
                  <span className="field-hint">Enter the time as it was in Singapore</span>
                )}
              </div>

              <div className="form-group">
                <label>Duration *</label>
                <div className="duration-inputs">
                  <div className="duration-field">
                    <input
                      type="number"
                      id="hours"
                      value={formData.hours}
                      onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                      min="0"
                      max="12"
                      required
                    />
                    <span className="duration-label">hr</span>
                  </div>
                  <div className="duration-field">
                    <input
                      type="number"
                      id="minutes"
                      value={formData.minutes}
                      onChange={(e) => setFormData({ ...formData, minutes: e.target.value })}
                      min="0"
                      max="59"
                      placeholder="0"
                    />
                    <span className="duration-label">min</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="remarks">Remarks</label>
              <textarea
                id="remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows="4"
                placeholder="Optional: Add any notes or comments about this lesson..."
              />
            </div>

            <button 
              type="submit" 
              disabled={loading || isMonthEndorsed(formData.date)} 
              className="submit-button"
            >
              {loading ? 'Submitting...' : isMonthEndorsed(formData.date) ? 'Month Endorsed - Cannot Submit' : 'Submit Attendance'}
            </button>
          </form>
        </div>

        <div className="recent-lessons-section">
          <div className="recent-header">
            <h2>Recent Submissions</h2>
            <span className="recent-count">{recentLessons.length}</span>
          </div>
          {showSGTBadge && (
            <p className="recent-tz-note">Times shown in Singapore Time (SGT)</p>
          )}
          {recentLessons.length === 0 ? (
            <div className="no-lessons">
              <p>No lessons submitted this month</p>
            </div>
          ) : (
            <div className="lessons-list">
              {recentLessons.map(lesson => (
                <div key={lesson._id} className="lesson-item">
                  <div className="lesson-main">
                    <div className="lesson-tutee-name">{lesson.tutee?.name}</div>
                    <div className="lesson-subject-name">{lesson.subject}</div>
                  </div>
                  <div className="lesson-meta">
                    <div className="meta-row">
                      <span className="meta-label">Conducted:</span>
                      <span className="meta-value">{formatDate(lesson.date)}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Mode:</span>
                      <span className={`mode-badge mode-${lesson.mode?.toLowerCase()}`}>
                        {lesson.mode}
                      </span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">Duration:</span>
                      <span className="meta-value">{formatDuration(lesson.duration)}</span>
                    </div>
                    <div className="meta-row">
                      <span className="meta-label">My Rate:</span>
                      <span className="meta-value meta-rate">${lesson.tutorRate || 0}</span>
                    </div>
                  </div>
                  {lesson.summary && (
                    <div className="lesson-remarks">
                      <span className="remarks-label">Remarks:</span>
                      <span className="remarks-text">{lesson.summary}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmitAttendance;
